// AddOrderFeedbackQuestion.jsx

import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import Sidebar from "../Sidebar";
import Layout from "../Layout";
import Loader from "../../Loader";
import LabledInput from "../CommonComponents/LabledInput";
import BackButton from "../CommonComponents/BackButton";
import {
  ADD_ORDER_FEEDBACK_QUESTION,
  UPDATE_ORDER_FEEDBACK_QUESTION,
} from "./OrderFeedbackQuestion.js";

const AddOrderFeedbackQuestion = ({ editFormData, setEditForm }) => {
  const [addQuestion, { loading }] = useMutation(ADD_ORDER_FEEDBACK_QUESTION);
  const [updateQuestion] = useMutation(UPDATE_ORDER_FEEDBACK_QUESTION);

  const [question, setQuestion] = useState(editFormData?.question || "");
  const [targetType, setTargetType] = useState(
    editFormData?.targetType || "ADM-EMP",
  );
  const [answerType, setAnswerType] = useState(
    editFormData?.answerType || "TEXT",
  );
  const [options, setOptions] = useState(
    editFormData?.options ? JSON.stringify(editFormData.options) : "",
  );
  const [minRating, setMinRating] = useState(
    editFormData?.minRating ?? "",
  );
  const [maxRating, setMaxRating] = useState(
    editFormData?.maxRating ?? "",
  );

  const isRatingGated = targetType === "SERVICE" || targetType === "PRODUCT";

  const handleSubmit = (e) => {
    e.preventDefault();

    const finalData = {
      question,
      targetType,
      answerType,
      options:
        answerType === "SELECT" || answerType === "MULTISELECT"
          ? JSON.parse(options || "[]")
          : null,
      minRating: isRatingGated && minRating !== "" ? Number(minRating) : null,
      maxRating: isRatingGated && maxRating !== "" ? Number(maxRating) : null,
    };

    if (editFormData) {
      finalData.id = editFormData.id;
      updateQuestion({ variables: { data: finalData } }).then(() => {
        window.location = "/admin/order-feedback-question-table";
      });
    } else {
      addQuestion({ variables: { data: finalData } }).then(() => {
        window.location = "/admin/order-feedback-question-table";
      });
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <Layout>
        <div className="p-6">
          <BackButton onClick={() => setEditForm(false)} />

          <h3 className="text-[#FBB033] text-2xl font-bold mb-6">
            {editFormData ? "Edit" : "Add"} Feedback Question
          </h3>

          <div className="space-y-6">
            <LabledInput
              label="Question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
            />

            <div>
              <label className="text-white">Target Type</label>
              <select
                className="w-full mt-2 p-2 border rounded"
                value={targetType}
                onChange={(e) => setTargetType(e.target.value)}
              >
                <option value="ADM-EMP">Admin to Employee</option>
                <option value="USER-EMP">User to Employee</option>
                <option value="CUSTOMER">Customer</option>
                <option value="SERVICE">
                  Service feedback (post-booking gate)
                </option>
                <option value="PRODUCT">
                  Product feedback (post-booking gate)
                </option>
              </select>
            </div>

            <div>
              <label className="text-white">Answer Type</label>
              <select
                className="w-full mt-2 p-2 border rounded"
                value={answerType}
                onChange={(e) => setAnswerType(e.target.value)}
              >
                <option value="TEXT">TEXT</option>
                <option value="BOOLEAN">BOOLEAN</option>
                <option value="RATING">RATING</option>
                <option value="SELECT">SELECT</option>
                <option value="MULTISELECT">MULTISELECT</option>
              </select>
            </div>

            {(answerType === "SELECT" || answerType === "MULTISELECT") && (
              <LabledInput
                label="Options (JSON array)"
                value={options}
                onChange={(e) => setOptions(e.target.value)}
                placeholder='["Option1","Option2"]'
              />
            )}

            {isRatingGated && (
              <div>
                <label className="text-white">
                  Show only for star rating between (optional)
                </label>
                <div className="flex gap-4 mt-2">
                  <input
                    type="number"
                    min={1}
                    max={5}
                    className="w-full p-2 border rounded"
                    placeholder="Min (e.g. 1)"
                    value={minRating}
                    onChange={(e) => setMinRating(e.target.value)}
                  />
                  <input
                    type="number"
                    min={1}
                    max={5}
                    className="w-full p-2 border rounded"
                    placeholder="Max (e.g. 3)"
                    value={maxRating}
                    onChange={(e) => setMaxRating(e.target.value)}
                  />
                </div>
                <p className="text-white/60 text-xs mt-1">
                  Leave blank for unbounded. E.g. 1-3 shows this question only
                  on low ratings; 4-5 shows it only on high ratings.
                </p>
              </div>
            )}

            <button
              onClick={handleSubmit}
              className="bg-[#FBB034] text-black px-6 py-2 rounded-md"
            >
              {editFormData ? "Update" : "Add"} Question
            </button>
          </div>
        </div>
      </Layout>
    </div>
  );
};

export default AddOrderFeedbackQuestion;
